import { useState, useRef } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface VoiceNoteInputProps {
  /** Used to namespace the audio file in storage */
  contextId: string;
  /** Called with the transcribed text after recording + transcription */
  onTranscript: (text: string) => void;
  disabled?: boolean;
}

export function VoiceNoteInput({
  contextId,
  onTranscript,
  disabled = false,
}: VoiceNoteInputProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingLabel, setProcessingLabel] = useState("");

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const processRecording = async (audioBlob: Blob) => {
    try {
      // 1. Upload to Supabase Storage
      setProcessingLabel("Uploading...");
      setIsProcessing(true);

      const filename = `${contextId}-${Date.now()}.webm`;

      const { error: uploadError } = await supabase.storage
        .from("audio-notes")
        .upload(filename, audioBlob, {
          contentType: "audio/webm",
          upsert: true,
        });

      if (uploadError) {
        console.error("Upload error:", uploadError);
        toast.error("Failed to upload audio");
        setIsProcessing(false);
        return;
      }

      // 2. Call transcribe-audio edge function
      setProcessingLabel("Transcribing...");

      const { data, error: fnError } = await supabase.functions.invoke(
        "transcribe-audio",
        { body: { filePath: filename } }
      );

      if (fnError) {
        console.error("Transcription error:", fnError);
        toast.error("Audio saved but transcription failed");
        setIsProcessing(false);
        return;
      }

      if (data?.transcript) {
        onTranscript(data.transcript);
        toast.success("Voice note transcribed");
      } else {
        toast.warning("Audio saved — no transcript returned");
      }

      setIsProcessing(false);
    } catch (err) {
      console.error("Error processing recording:", err);
      toast.error("Failed to process recording");
      setIsProcessing(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm",
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((track) => track.stop());
        await processRecording(blob);
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("Could not access microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const isDisabled = disabled || isProcessing;

  // Recording state — show stop button
  if (isRecording) {
    return (
      <Button
        size="sm"
        variant="destructive"
        onClick={stopRecording}
        className="gap-1.5 h-7"
      >
        <Square className="h-3 w-3" />
        Stop ({formatTime(recordingTime)})
      </Button>
    );
  }

  // Processing state — show spinner
  if (isProcessing) {
    return (
      <Button size="sm" variant="outline" disabled className="gap-1.5 h-7">
        <Loader2 className="h-3 w-3 animate-spin" />
        {processingLabel}
      </Button>
    );
  }

  // Idle state — show mic button
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={startRecording}
      disabled={isDisabled}
      className="gap-1.5 h-7"
    >
      <Mic className="h-3 w-3" />
      Voice
    </Button>
  );
}
